import { learnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";

import { flashcardQueueSuccessor } from "#flashcard-queue";
import { failedLearnCardOperationId } from "#learn-deck-reducer";
import type { LearnDeckState } from "#learn-deck-state";

/**
 * What the deck does with a released swipe on `presentationKey` right now:
 *
 *   * `commit` — it is the current card and the deck can act on it (or its own action is under
 *     way, and the commit path decides: a write retry, or nothing);
 *   * `hold` — it will be able to soon: it is the next flashcard a released card uncovered, waiting
 *     for that card's exit, or it is current but its controls are still registering or a flip or
 *     view switch is finishing;
 *   * `refuse` — it never will (another card took its place, or the action ahead of it failed).
 *
 * A swipe is never dropped for a reason that is about to pass: that is how a quick second swipe
 * used to vanish while the first card's write was in flight.
 */
export function learnDeckSwipeHold(
  state: LearnDeckState,
  presentationKey: string,
  hasControls: boolean,
): "commit" | "hold" | "refuse" {
  const { phase } = state.transition;
  if (learnCardPresentationKey(state.current.presentation) === presentationKey) {
    if (phase === "flipping" || phase === "switching-view") {
      return "hold";
    }
    if (phase !== "idle" && phase !== "dragging") {
      return "commit";
    }
    return hasControls ? "commit" : "hold";
  }
  const successor = flashcardQueueSuccessor(state);
  const isUncoveredSuccessor =
    successor?.kind === "card" &&
    learnCardPresentationKey(successor.slot.presentation) === presentationKey;
  return isUncoveredSuccessor && phase === "committed" && failedLearnCardOperationId(state) === null
    ? "hold"
    : "refuse";
}
